import { apiHelper } from '@/utils'
import { API } from '@/constants'

/**
 * The knowledge base — proxied through Laravel to ai_bot, which owns it.
 *
 * Unlike the inbox, these paths DO carry a tenant id: ai_bot addresses a KB by
 * tenant and this app is a multi-tenant console. The id is authorised server-side
 * against the logged-in agent (FaqController::guard), never trusted from here.
 */
export const faqService = {
  list,
  get,
  save,
  remove,
  publish,
  getStandard,
  runStandard,
  audit,
  unanswered,
  followups,
  quickTriggers,
  removeFollowup,
}

const API_URL = API.BASE_URL + '/v1'

/**
 * Scope every call to the tenant in its own path.
 *
 * The interceptor otherwise stamps whatever the brand picker last selected,
 * which is right for every other screen and wrong here: copying a document from
 * one brand to another reads A and writes B in one action, and a header fixed
 * to the selected brand makes the server refuse the half that is not it.
 *
 * Still authorised server-side — FaqController::guard runs TenantContext::allows
 * on the id either way, so naming a brand here cannot reach one the agent does
 * not have.
 */
function forTenant(tenantId) {
  return { headers: { 'X-Tenant-Id': String(tenantId) } }
}

/** Each row carries `sections` — how many `##` chunks the bot will retrieve. */
export async function list(tenantId) {
  return apiHelper(API_URL).get(`/tenants/${tenantId}/faq`, forTenant(tenantId))
}

/** Returns the document AND its section titles, so the editor can show the
 *  author what the bot will actually retrieve rather than what they typed. */
export async function get(tenantId, docId) {
  return apiHelper(API_URL).get(`/tenants/${tenantId}/faq/${docId}`, forTenant(tenantId))
}

/**
 * Save a draft.
 *
 * Saving is NOT publishing: the bot keeps answering from the last ingest. The
 * response carries a `note` saying so and the UI must surface it — otherwise
 * someone edits the deposit limit and assumes it took effect.
 */
export async function save(tenantId, docId, payload) {
  return apiHelper(API_URL).put(`/tenants/${tenantId}/faq/${docId}`, payload, forTenant(tenantId))
}

export async function remove(tenantId, docId) {
  return apiHelper(API_URL).delete(`/tenants/${tenantId}/faq/${docId}`, forTenant(tenantId))
}

/**
 * Check the FAQ against itself.
 *
 * Two kinds of finding: structural ones that are exact and free (a document
 * with no `## ` headings produces nothing retrievable; a draft published with
 * its [placeholders] still in it), and semantic ones that cost an embedding
 * pass plus one model call — sections that CONTRADICT each other.
 *
 * That last one is the reason this exists. The Bot health reports read
 * telemetry, so they can only see what players have already tripped over. Two
 * sections answering "minimum deposit" with different numbers trips nothing:
 * retrieval picks one by score, every turn is confidently grounded, and the gap
 * report stays empty while half the players are told the wrong amount.
 */
/**
 * The follow-up questions the last publish attached to FAQ sections — "are
 * you 18 or older?" after the minimum-age answer. Decided once at publish by
 * ai_bot (eligibility requirements only), shown on the question so the
 * author can see what the bot will ask, and removable.
 */
export async function followups(tenantId) {
  return apiHelper(API_URL).get(`/tenants/${tenantId}/faq/followups`, forTenant(tenantId))
}

/**
 * The one-word triggers each question answers outright, as the last publish
 * settled them — the author's own words, or the ones the bot proposed for a
 * question whose author ticked the box and left it blank.
 */
export async function quickTriggers(tenantId) {
  return apiHelper(API_URL).get(`/tenants/${tenantId}/faq/quick-triggers`, forTenant(tenantId))
}

export async function removeFollowup(tenantId, chunkId) {
  return apiHelper(API_URL).delete(
    `/tenants/${tenantId}/faq/followups/${encodeURIComponent(chunkId)}`,
    forTenant(tenantId)
  )
}

export async function audit(tenantId) {
  return apiHelper(API_URL).get(`/tenants/${tenantId}/faq/audit`, forTenant(tenantId))
}

/**
 * Re-embed and go live. Atomic on ai_bot's side — a failure partway leaves the
 * previous KB serving. Refused when there is nothing active to publish.
 *
 * Also refused, with 409 and a `blocking` list, when the audit found problems
 * that make the bot answer wrongly: a section still carrying a TODO marker, a
 * section with no content, two headings competing for the same question.
 * `force` publishes anyway — a brand that needs one corrected figure live now
 * should not be held up by an unrelated finding — but it has to be chosen.
 */
export async function publish(tenantId, force = false) {
  return apiHelper(API_URL).post(
    `/tenants/${tenantId}/faq/publish${force ? '?force=1' : ''}`,
    null,
    forTenant(tenantId)
  )
}

/**
 * Questions players asked that this FAQ could not answer.
 *
 * Cheap — one indexed GROUP BY on ai_bot's side, no model call — so the editor
 * loads it on open rather than hiding it behind a button somebody has to know
 * to press.
 */
export async function unanswered(tenantId, days = 30) {
  return apiHelper(API_URL).get(`/tenants/${tenantId}/faq/unanswered`, {
    ...forTenant(tenantId),
    params: { days },
  })
}

/**
 * The last four-layer check, and what another one would cost.
 *
 * `estimated_turns` is the point of returning this before anything runs: the
 * check asks the bot every FAQ section twice and the whole golden set twice,
 * which is real spend on the brand, and the button has to say so before it is
 * pressed rather than after.
 */
export async function getStandard(tenantId) {
  return apiHelper(API_URL).get(`/tenants/${tenantId}/faq/standard`, forTenant(tenantId))
}

/**
 * Start one. Answers 202 and the screen polls getStandard().
 *
 * Minutes, not seconds. 409 means a run is already going — the right response
 * to that is to show that run, not to retry.
 */
export async function runStandard(tenantId) {
  return apiHelper(API_URL).post(`/tenants/${tenantId}/faq/standard`, null, forTenant(tenantId))
}
